import ScrollModal from "@/components/common/scroll-modal";
import Card from "@/pages/extension/modal/Card";
import type { SuspensionRequest } from "@/queries/deadline-suspensions/deadline-suspensions.api";
import { getRemainingTime } from "@/utils/procedures-ui";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  FileItem,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useState } from "react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApprove: () => void;
  onDenied: () => void;
  onSendToLead: () => void;
  data: SuspensionRequest;
  canSendToLead?: boolean;
  canAction?: boolean;
}

export const ApproveSuspensionInfo = ({
  isOpen,
  onClose,
  onApprove,
  onDenied,
  onSendToLead,
  data,
  canAction,
  canSendToLead,
}: Props) => {
  const { showToast } = useToast();

  const needLeaderApproval =
    !!data?.formality_request?.requires_leader_approval;

  const externalDeadline = useMemo<Date | null>(() => {
    if (!data?.formality_request?.applicant_deadline) return null;
    try {
      return new Date(data.formality_request.applicant_deadline);
    } catch {
      return null;
    }
  }, [data?.formality_request?.applicant_deadline]);

  const initialTimeExpired = useMemo(() => {
    if (!externalDeadline) return false;
    return externalDeadline.getTime() <= new Date().getTime();
  }, [externalDeadline]);

  const initialRemainingTime = useMemo(() => {
    if (!externalDeadline) return "";
    if (initialTimeExpired) return "0d 0h 0m";
    return getRemainingTime(externalDeadline);
  }, [externalDeadline, initialTimeExpired]);

  const [currentRemainingTime, setCurrentRemainingTime] =
    useState<string>(initialRemainingTime);
  const [timeExpired, setTimeExpired] = useState<boolean>(initialTimeExpired);

  /**
   * Effect to handle countdown timer for applicant deadline
   */
  useEffect(() => {
    if (!externalDeadline || timeExpired || !isOpen) return;

    const intervalId = setInterval(() => {
      const now = new Date();

      if (externalDeadline.getTime() <= now.getTime()) {
        setCurrentRemainingTime("0d 0h 0m");
        setTimeExpired(true);
        clearInterval(intervalId);
        return;
      }

      const updatedRemainingTime = getRemainingTime(externalDeadline);
      setCurrentRemainingTime(updatedRemainingTime);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [externalDeadline, timeExpired, isOpen]);

  const handleOpenFile = (fileUrl?: string) => {
    if (!fileUrl) return;
    try {
      if (!fileUrl) {
        showToast({
          title: "Error",
          description: "La URL del archivo no está disponible",
          options: {
            type: "warning",
          },
        });
        return;
      }
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    } catch {
      showToast({
        title: "Error",
        description: "Error al abrir el archivo",
        options: {
          type: "error",
        },
      });
    }
  };

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-[520px] h-[680px] max-w-[520px] max-h-[680px] rounded-xl"
      header={
        <div className="py-3 px-8">
          <div className="flex items-center justify-between w-full">
            <div className="text-center text-h2! font-semibold text-heading">
              <Heading variant="h5">Responder solicitud de plazo</Heading>
            </div>
            <Button
              variant="quiet"
              size="icon"
              aria-label="Cerrar"
              onClick={onClose}
              className="px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <>
          {canAction ? (
            <div
              className={`w-full grid grid-cols-2 tablet:grid-cols-2 py-3 px-8 gap-3`}
            >
              {canAction && !needLeaderApproval && (
                <div>
                  <Button
                    size="lg"
                    type="button"
                    variant="danger-tertiary"
                    onClick={onDenied}
                  >
                    Denegar
                  </Button>
                </div>
              )}
              <div className="flex gap-3 justify-end">
                {canAction && canSendToLead && (
                  <Button
                    size="lg"
                    type="button"
                    variant="tertiary"
                    onClick={onSendToLead}
                  >
                    Enviar al líder
                  </Button>
                )}
                {canAction && !needLeaderApproval && (
                  <Button size="lg" type="button" onClick={onApprove}>
                    Aprobar
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
        </>
      }
    >
      <div className="flex flex-col flex-1 min-h-0 px-4 sm:px-8 gap-4">
        <Card title="Tipo de aplazamiento">
          <div className="w-full flex items-center gap-3 px-3 py-2 bg-neutral-object rounded-lg">
            <p className="font-normal leading-3xl text-body">Suspensión</p>
          </div>
        </Card>

        <Card title="Solicitante">
          <div className="w-full flex items-center gap-3 px-3 py-2 bg-neutral-object rounded-lg">
            <Avatar size="sm" shape="circle" color="action">
              <AvatarFallback color="action">
                {data?.formality_request?.applicant?.first_name
                  ?.charAt(0)
                  .toUpperCase() ||
                  data?.formality_request?.applicant?.email
                    ?.charAt(0)
                    .toUpperCase() ||
                  "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-md font-semibold leading-3xl text-body">
                {`${data?.formality_request?.applicant?.first_name || ""} ${data?.formality_request?.applicant?.last_name || ""}`.trim()}
              </p>
              <p className="text-sm font-normal leading-2xl text-body-quiet">
                {data?.formality_request?.applicant?.email}
              </p>
            </div>
          </div>
        </Card>

        <Card title="Nombre del paso">
          <div className="w-full flex justify-between flex-col md:flex-row md:items-center gap-3 px-3 py-2 bg-neutral-object rounded-lg">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Avatar size="2xs" shape="circle" color="action">
                <AvatarFallback color="action">
                  <Icon type="far" name="bars" size="xs" />
                </AvatarFallback>
              </Avatar>
              <span className="font-normal text-md leading-3xl text-body truncate">
                {data?.formality_request?.flowExecution?.active_node_execution
                  ?.node?.display_name || ""}
              </span>
            </div>
            {externalDeadline && (
              <Badge
                size="sm"
                shape="square"
                variant={timeExpired ? "danger" : "warning"}
                fill="ghost"
                className="flex items-center w-fit"
              >
                {timeExpired ? "0d 0h 0m" : currentRemainingTime}
                <Icon
                  type="fas"
                  name="clock"
                  size="xs"
                  className={
                    timeExpired ? "text-danger-quiet" : "text-warning-quiet"
                  }
                />
              </Badge>
            )}
          </div>
        </Card>

        <Card title="Comentario">
          <p className="font-normal text-md leading-3xl text-body">
            {data.observation || "No hay comentarios"}
          </p>
        </Card>

        {data?.files && data?.files?.length > 0 && (
          <Card title="Archivo de evidencia">
            <div className="flex flex-col gap-2">
              {data.files.map((file, index) => (
                <FileItem
                  key={file.name + index}
                  fileName={file.name}
                  actionAriaLabel={`Descargar archivo de evidencia ${index + 1}`}
                  onAction={() => {
                    handleOpenFile(file.url);
                  }}
                  tag={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                  action={
                    <Icon
                      type="far"
                      name="arrow-up-right"
                      size="sm"
                      className="text-body-loud"
                    />
                  }
                />
              ))}
            </div>
          </Card>
        )}
      </div>
    </ScrollModal>
  );
};
